/* Champ Student — Millionaire-style game player + body for SubjectDetail.
 *
 * Single file: MillionaireBody (tool body) + ChampStudentPlayer (fullscreen player).
 * Used both as a SubjectDetail tool and shared into the live class.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { correctIndexOf } from "@/lib/quizUtils";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import {
  X, Loader2, Wand2, Crown, GraduationCap, Trash2, Sparkles, FastForward, Bot, Volume2, VolumeX, Phone, PhoneCall,
} from "lucide-react";

// 13-step prize ladder (top → bottom display order)
const LADDER = [
  { i: 13, prize: "$1M", safe: false },
  { i: 12, prize: "$500K", safe: false },
  { i: 11, prize: "$250K", safe: false },
  { i: 10, prize: "$100K", safe: false },
  { i: 9, prize: "$50K", safe: true },
  { i: 8, prize: "$20K", safe: false },
  { i: 7, prize: "$10K", safe: false },
  { i: 6, prize: "$5K", safe: false },
  { i: 5, prize: "$2K", safe: true },
  { i: 4, prize: "$1K", safe: false },
  { i: 3, prize: "$500", safe: false },
  { i: 2, prize: "$200", safe: false },
  { i: 1, prize: "$100", safe: false },
];
const QUESTION_SECONDS = 24;

export function MillionaireBody({ chapter, subject }) {
  const [games, setGames] = useState([]);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(null);

  const reload = async () => {
    try {
      const { data } = await api.get("/ai/millionaire", { params: { chapter_id: chapter.id } });
      setGames(data.games || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [chapter.id]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/millionaire/generate", {
        subject: subject.name, topic: chapter.title, grade: subject.grade_name || "",
        subject_id: subject.id, chapter_id: chapter.id,
        pages: (chapter.pages || []).map((p) => ({ order: p.order, ocr_text: p.ocr_text, summary: p.summary })),
        n: 13,
      });
      toast.success(`Champ Student ready 👑 ${data.game.questions.length} questions`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Game gen failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this game?")) return;
    try { await api.delete(`/ai/millionaire/${id}`); reload(); toast.success("Deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const hasPages = (chapter.pages || []).length > 0;
  return (
    <div data-testid="millionaire-body">
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] font-bold" style={{ color: "#A855F7" }}>Game</div>
      <h2 className="font-display font-bold text-2xl tracking-tighter mt-1 inline-flex items-center gap-2">
        Champ Student <Crown className="text-[#FFD600]" size={22} />
      </h2>
      <p className="text-sm text-neutral-500 mt-1">A "Who Wants to Be a Millionaire"-style show. Prize ladder, lifelines, suspense clock.</p>
      <button
        onClick={generate}
        disabled={!hasPages || busy}
        data-testid="millionaire-generate"
        className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        {busy ? <><Loader2 size={14} className="animate-spin" /> Building game…</> : <><Wand2 size={14} /> Build new game</>}
      </button>
      {!hasPages && <div className="text-[11px] text-neutral-500 mt-2">Scan a chapter page first.</div>}

      {games.length > 0 && (
        <div className="mt-7">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-3">Saved games · {games.length}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {games.map((g) => (
              <div key={g.id} className="bg-gradient-to-br from-[#1A0F2E] via-[#2D1B4E] to-[#1E1B3D] text-white border border-[#A855F7]/40 rounded-md p-3 group">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#FFD600] font-bold">{(g.questions || []).length} questions</div>
                <div className="font-display font-bold text-sm tracking-tighter mt-1 line-clamp-2 leading-snug">{g.title}</div>
                <div className="mt-3 flex items-center gap-2">
                  <button onClick={() => setPlaying(g)} data-testid={`millionaire-play-${g.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-[#FFD600] text-[#0A0C10] text-xs font-bold hover:bg-[#FFC600]">
                    <GraduationCap size={11} /> Play
                  </button>
                  <button onClick={() => remove(g.id)} className="text-white/40 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ChampStudentPlayer game={playing} onClose={() => setPlaying(null)} />
    </div>
  );
}

/* ───────────────────────── Champ Student player (fullscreen) ───────────────────────── */
export function ChampStudentPlayer({ game: gameProp, onClose, playerName = "Student", onProgress, onFinish, initialState }) {
  // Normalise question docs so `correct_index` always exists (some quizzes
  // only carry `correct_answer` text, which broke reveal/advance logic).
  const game = useMemo(() => gameProp ? ({
    ...gameProp,
    questions: (gameProp?.questions || []).map((qq) => ({ ...qq, correct_index: correctIndexOf(qq) })),
  }) : null, [gameProp?.id]);
  const [idx, setIdx] = useState(initialState?.current_q || 0);
  const [picked, setPicked] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [timer, setTimer] = useState(QUESTION_SECONDS);
  const [muted, setMuted] = useState(false);
  const [lifelines, setLifelines] = useState(initialState?.lifelines || { fifty: true, ask: true, skip: true });
  const [hiddenOptions, setHiddenOptions] = useState([]);
  const [askHint, setAskHint] = useState("");
  const [dial, setDial] = useState(null); // null | "dialing" | "talking"
  const [result, setResult] = useState(null);
  const tickRef = useRef(null);
  const audioRef = useRef(null);
  const answersRef = useRef(initialState?.answers || {});
  const clearedRef = useRef(initialState?.cleared || 0);
  const finishedRef = useRef(false);
  const lifelinesRef = useRef(lifelines);
  lifelinesRef.current = lifelines;
  const autoNextRef = useRef(null); // auto-advance timer after a correct answer
  const nextFnRef = useRef(null);
  // Cancel a pending auto-advance if the player/overlay unmounts
  useEffect(() => () => { if (autoNextRef.current) clearTimeout(autoNextRef.current); }, []);

  const emitFinish = (res) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onFinish?.({ ...res, answers: { ...answersRef.current } });
  };

  useEffect(() => {
    if (game) {
      const st = initialState || {};
      const lf = st.lifelines || { fifty: true, ask: true, skip: true };
      setIdx(st.current_q || 0); setPicked(null); setRevealed(false); setTimer(QUESTION_SECONDS); setHiddenOptions([]); setAskHint(""); setDial(null); setResult(null); setLifelines(lf);
      answersRef.current = st.answers || {}; clearedRef.current = st.cleared || 0; finishedRef.current = false;
      // Announce the starting position immediately so live mirrors render it
      onProgress?.({ answers: { ...answersRef.current }, cleared: clearedRef.current, current_q: st.current_q || 0, status: "playing", lifelines: lf, picked: null, revealed: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.id]);

  // Question timer — pauses while Dial Mobi is active
  useEffect(() => {
    if (!game || result) return;
    if (!game || revealed || dial) { clearInterval(tickRef.current); return; }
    tickRef.current = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(tickRef.current);
          setRevealed(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, revealed, game?.id, result, dial]);

  // Tick sound — uses web audio API for an exact tick
  useEffect(() => {
    if (!game || muted || revealed || result || dial) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.value = 880; o.type = "sine";
      g.gain.value = 0.06;
      o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + 0.05);
      setTimeout(() => ctx.close(), 100);
    } catch { /* ignore */ }
  }, [timer, muted, revealed, game, result]);

  if (!gameProp) return null;
  const total = game.questions.length;
  const q = game.questions[idx];
  const ladderTier = LADDER[Math.max(0, LADDER.length - 1 - idx)];

  if (!q) {
    return (
      <div className="fixed inset-0 z-[95] bg-[#070B14] flex flex-col items-center justify-center gap-4 text-white" data-testid="champ-empty-game">
        <div className="text-sm text-white/60">This game has no questions yet — regenerate it and try again.</div>
        <button onClick={onClose} className="px-5 py-2.5 rounded-md border border-white/25 text-sm hover:bg-white/10">Close</button>
      </div>
    );
  }

  const pick = (i) => {
    if (revealed || picked != null) return;
    setPicked(i);
    answersRef.current = { ...answersRef.current, [idx]: i };
    setTimeout(() => {
      setRevealed(true);
      if (i === q.correct_index) {
        clearedRef.current += 1;
        confetti({ particleCount: 40, spread: 70, origin: { y: 0.5 }, colors: ["#FFD600", "#A855F7", "#EC4899"] });
        try { if (!muted) { const u = new SpeechSynthesisUtterance("Correct!"); u.pitch = 1.4; window.speechSynthesis.speak(u); } } catch {}
        // Correct → auto-advance to the next question after a short celebration
        autoNextRef.current = setTimeout(() => { autoNextRef.current = null; nextFnRef.current?.(); }, 2200);
      }
      onProgress?.({ answers: { ...answersRef.current }, cleared: clearedRef.current, current_q: idx, status: "playing", lifelines: lifelinesRef.current, picked: i, revealed: true });
    }, 1000);
  };

  const next = () => {
    if (autoNextRef.current) { clearTimeout(autoNextRef.current); autoNextRef.current = null; }
    if (picked === q.correct_index && idx < total - 1) {
      setIdx(idx + 1); setPicked(null); setRevealed(false); setTimer(QUESTION_SECONDS); setHiddenOptions([]); setAskHint("");
      onProgress?.({ answers: { ...answersRef.current }, cleared: clearedRef.current, current_q: idx + 1, status: "playing", lifelines: lifelinesRef.current, picked: null, revealed: false });
    } else {
      // Game over — compute winnings (safe-haven floor for wrong answers)
      const isWin = picked === q.correct_index && idx === total - 1;
      let earned = "$0";
      if (isWin) earned = LADDER[0].prize;
      else if (picked !== q.correct_index) {
        // floor to last safe tier passed
        const cleared = idx;  // tiers cleared = idx (current was failed)
        for (let i = LADDER.length - 1; i >= 0; i--) {
          const tier = LADDER[i];
          const tierIdx = LADDER.length - 1 - i; // 0-based progression idx
          if (tier.safe && tierIdx < cleared) earned = tier.prize;
        }
      } else {
        // ran out of questions but got correct
        earned = LADDER[Math.max(0, LADDER.length - 1 - idx)].prize;
      }
      const res = { win: isWin, earned, cleared: idx + (picked === q.correct_index ? 1 : 0) };
      setResult(res);
      emitFinish(res);
      confetti({ particleCount: 120, spread: 110, origin: { y: 0.5 }, colors: ["#FFD600", "#A855F7", "#EC4899", "#10B981"] });
    }
  };
  nextFnRef.current = next;

  const useFifty = () => {
    if (!lifelines.fifty || revealed) return;
    const nl = { ...lifelinesRef.current, fifty: false };
    setLifelines(nl);
    const wrongs = q.options.map((_, i) => i).filter((i) => i !== q.correct_index);
    const removeTwo = wrongs.sort(() => Math.random() - 0.5).slice(0, 2);
    setHiddenOptions(removeTwo);
    onProgress?.({ answers: { ...answersRef.current }, cleared: clearedRef.current, current_q: idx, status: "playing", lifelines: nl, picked, revealed: false, hidden: removeTwo });
  };
  // Dial Mobi — one-time lifeline. Pauses the clock, plays a dialing
  // animation, then Mobi answers in a speech bubble with a strong hint.
  const dialMobi = () => {
    if (!lifelines.ask || revealed || dial) return;
    const nl = { ...lifelinesRef.current, ask: false };
    setLifelines(nl);
    setDial("dialing");
    onProgress?.({ answers: { ...answersRef.current }, cleared: clearedRef.current, current_q: idx, status: "playing", lifelines: nl, picked, revealed: false, dial: "dialing" });
    const optionsList = q.options.map((o, i) => `${"ABCD"[i]}) ${o}`).join(" ; ");
    const wrongs = q.options.map((_, i) => i).filter((i) => i !== q.correct_index);
    const fallback = `Hey ${playerName}! I'd confidently rule out "${q.options[wrongs[0]]}"${wrongs[1] != null ? ` and "${q.options[wrongs[1]]}"` : ""}. Think about what's left — you've got this!`;
    const hintPromise = api.post("/ai/chat", {
      message: `You are Mobi, a friendly AI tutor taking a phone call from a student named ${playerName} during a quiz game. The correct answer is "${q.options[q.correct_index]}". Reply in 1-2 short sentences: greet ${playerName} by name and give a STRONG hint that clearly guides them toward the correct answer — do NOT say the option letter and do NOT quote the option text verbatim.\n\nQuestion: ${q.question}\nOptions: ${optionsList}`,
      persona: "tutor",
    }).then((r) => (r?.data?.reply || r?.data?.content || "").trim()).catch(() => "");
    setTimeout(async () => {
      const reply = (await hintPromise) || fallback;
      setAskHint(reply);
      setDial("talking");
      onProgress?.({ answers: { ...answersRef.current }, cleared: clearedRef.current, current_q: idx, status: "playing", lifelines: lifelinesRef.current, picked, revealed: false, dial: "talking", hint: reply });
    }, 2800);
  };
  const useSkip = () => {
    if (!lifelines.skip || revealed) return;
    const nl = { ...lifelinesRef.current, skip: false };
    setLifelines(nl);
    if (idx < total - 1) {
      setIdx(idx + 1); setPicked(null); setRevealed(false); setTimer(QUESTION_SECONDS); setHiddenOptions([]); setAskHint("");
      onProgress?.({ answers: { ...answersRef.current }, cleared: clearedRef.current, current_q: idx + 1, status: "playing", lifelines: nl, picked: null, revealed: false });
    }
  };

  if (result) {
    return (
      <div className="fixed inset-0 z-[80] bg-gradient-to-br from-[#1A0F2E] via-[#2D1B4E] to-[#0A0C10] text-white flex flex-col items-center justify-center" data-testid="champ-result">
        <Crown size={56} className="text-[#FFD600] mb-4" />
        <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#FFD600] font-bold">/ champ student · finished</div>
        <h1 className="font-display font-bold text-6xl tracking-tighter mt-3">{result.earned}</h1>
        <p className="text-white/70 mt-2 text-lg">{result.win ? "You're the Champ! 👑" : `Cleared ${result.cleared} / ${total} questions.`}</p>
        <button onClick={onClose} className="mt-8 px-6 py-3 rounded-md bg-[#FFD600] text-[#0A0C10] font-bold inline-flex items-center gap-2" data-testid="champ-close">
          <X size={14} /> Close
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] bg-gradient-to-br from-[#1A0F2E] via-[#2D1B4E] to-[#1E1B3D] text-white" data-testid="champ-overlay">
      {/* Header */}
      <div className="absolute top-4 left-5 right-5 flex items-center justify-between z-10">
        <div className="inline-flex items-center gap-2">
          <GraduationCap size={16} className="text-[#FFD600]" />
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] font-bold">Champ Student</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setMuted((m) => !m)} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center" data-testid="champ-mute">
            {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
          </button>
          <button onClick={() => { emitFinish({ win: false, earned: "$0", cleared: clearedRef.current }); onClose(); }} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center" data-testid="champ-exit"><X size={14} /></button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_280px] h-full pt-20 pb-6 px-6 lg:px-10 gap-6">
        {/* Question area */}
        <div className="flex flex-col justify-between min-w-0">
          <div className="flex items-center justify-between mb-6">
            <div className="text-sm font-mono uppercase tracking-[0.22em] text-white/70">QUESTION {idx + 1} OF {total}</div>
            <div className="flex items-center gap-3">
              <Timer seconds={timer} />
              <div className="px-3 py-1.5 bg-[#FFD600]/20 border border-[#FFD600]/40 rounded-md text-[#FFD600] text-sm font-bold font-mono">For {ladderTier.prize}</div>
            </div>
          </div>

          {/* Question card */}
          <AnimatePresence mode="wait">
            <motion.div key={idx}
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-gradient-to-br from-[#1E40AF] to-[#0F2A6B] border border-[#FFD600]/30 rounded-2xl p-8 lg:p-10 text-center shadow-[0_0_60px_-15px_rgba(255,214,0,0.4)]"
            >
              <div className="font-display font-bold text-2xl lg:text-3xl leading-snug">{q.question}</div>
            </motion.div>
          </AnimatePresence>

          {/* Options 2x2 */}
          <div className="grid sm:grid-cols-2 gap-3 mt-6">
            {q.options.map((opt, i) => {
              const hidden = hiddenOptions.includes(i);
              const isPicked = picked === i;
              const isCorrect = revealed && i === q.correct_index;
              const isWrong = revealed && isPicked && i !== q.correct_index;
              return (
                <button
                  key={i} onClick={() => pick(i)} disabled={revealed || hidden}
                  data-testid={`champ-opt-${i}`}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    hidden ? "opacity-20 cursor-not-allowed bg-[#1E40AF]/30 border-[#1E40AF]" :
                    isCorrect ? "bg-[#10B981] border-[#10B981] scale-[1.02]" :
                    isWrong ? "bg-red-600 border-red-600" :
                    isPicked ? "bg-[#FFD600] text-[#0A0C10] border-[#FFD600]" :
                    "bg-gradient-to-br from-[#1E40AF]/80 to-[#0F2A6B]/80 border-[#FFD600]/30 hover:border-[#FFD600] hover:scale-[1.01]"
                  }`}
                >
                  <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-bold text-sm mr-3 ${isCorrect || isWrong || isPicked ? "bg-white/20" : "bg-[#FFD600] text-[#0A0C10]"}`}>{"ABCD"[i]}</span>
                  <span className="font-medium">{hidden ? "—" : opt}</span>
                </button>
              );
            })}
          </div>

          {/* Lifelines + hint */}
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <Lifeline label="50:50" disabled={!lifelines.fifty || revealed} onClick={useFifty} color="#FFD600" textColor="#0A0C10" testid="champ-fifty" />
            <Lifeline label="Dial Mobi" icon={Phone} disabled={!lifelines.ask || revealed || !!dial} onClick={dialMobi} color="#22D3EE" textColor="#0A0C10" testid="champ-dial-mobi" />
            <Lifeline label="Skip" icon={FastForward} disabled={!lifelines.skip || revealed} onClick={useSkip} color="#EC4899" textColor="#fff" testid="champ-skip" />
            {revealed && (
              <button onClick={next} data-testid="champ-next" className="ml-auto px-6 py-2.5 rounded-md bg-[#FFD600] text-[#0A0C10] font-bold inline-flex items-center gap-2 hover:bg-[#FFC600]">
                {picked === q.correct_index ? (idx < total - 1 ? "Next" : "Claim prize") : "End game"}
              </button>
            )}
          </div>
          {askHint && (
            <div className="mt-3 px-4 py-2 bg-[#22D3EE]/15 border border-[#22D3EE]/30 rounded-md text-sm">
              <span className="text-[#22D3EE] font-mono text-[10px] uppercase tracking-wider mr-2">Mobi says:</span>{askHint}
            </div>
          )}
          {revealed && q.explanation && picked !== q.correct_index && (
            <div className="mt-2 text-sm text-white/70 italic">{q.explanation}</div>
          )}
        </div>

        {/* Prize ladder */}
        <div className="hidden lg:flex flex-col bg-gradient-to-b from-[#0A0C10] to-[#1A0F2E] rounded-xl border border-[#FFD600]/30 p-3 overflow-hidden" data-testid="champ-ladder">
          <div className="text-center text-[11px] font-mono uppercase tracking-[0.22em] text-[#FFD600] font-bold mb-2">Prize Ladder</div>
          <div className="space-y-0.5 overflow-y-auto">
            {LADDER.map((tier) => {
              const isCurrent = tier.i === (idx + 1);
              return (
                <div key={tier.i} className={`flex items-center justify-between px-3 py-1.5 rounded text-sm transition-all ${
                  isCurrent ? "bg-[#FFD600] text-[#0A0C10] scale-[1.04] shadow-md" :
                  tier.safe ? "bg-[#7C3AED]/30 text-[#FFD600] font-mono" :
                  "text-white/60 font-mono"
                }`}>
                  <span className="font-bold text-xs w-6">{tier.i}</span>
                  <span className="font-bold">{tier.prize}</span>
                </div>
              );
            })}
          </div>
          <div className="text-center text-[10px] text-white/40 mt-1">Safe havens · <span className="text-[#FFD600]">$2K · $50K</span></div>
        </div>
      </div>

      {/* Dial Mobi — dialing animation + speech bubble. Timer is paused
          while this overlay is up. */}
      <AnimatePresence>
        {dial && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-30 bg-black/75 backdrop-blur-sm flex items-center justify-center px-4"
            data-testid="dial-mobi-overlay"
          >
            {dial === "dialing" ? (
              <div className="text-center">
                <div className="relative w-24 h-24 mx-auto">
                  <motion.span
                    className="absolute inset-0 rounded-full border-2 border-[#22D3EE]"
                    animate={{ scale: [1, 1.8], opacity: [0.7, 0] }}
                    transition={{ repeat: Infinity, duration: 1.1, ease: "easeOut" }}
                  />
                  <motion.span
                    className="absolute inset-0 rounded-full border-2 border-[#22D3EE]"
                    animate={{ scale: [1, 1.8], opacity: [0.7, 0] }}
                    transition={{ repeat: Infinity, duration: 1.1, ease: "easeOut", delay: 0.4 }}
                  />
                  <motion.div
                    animate={{ rotate: [0, -14, 14, -14, 0] }}
                    transition={{ repeat: Infinity, duration: 0.7 }}
                    className="absolute inset-0 rounded-full bg-[#22D3EE] flex items-center justify-center shadow-[0_0_50px_rgba(34,211,238,0.6)]"
                  >
                    <PhoneCall size={34} className="text-[#0A0C10]" />
                  </motion.div>
                </div>
                <div className="mt-6 text-white font-display font-bold text-2xl tracking-tighter">Dialing Mobi…</div>
                <div className="text-[#22D3EE] text-sm mt-1 font-medium">{playerName} is calling for help</div>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white/60 px-3 py-1.5 border border-white/15 rounded-full">
                  ⏸ Timer paused
                </div>
              </div>
            ) : (
              <motion.div
                initial={{ scale: 0.92, y: 12, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }}
                className="w-full max-w-md bg-[#10141C] border border-[#22D3EE]/40 rounded-2xl p-6 shadow-[0_0_70px_-10px_rgba(34,211,238,0.5)]"
                data-testid="dial-mobi-bubble"
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#22D3EE] to-[#6366F1] flex items-center justify-center shrink-0">
                    <Bot size={20} className="text-white" />
                  </div>
                  <div>
                    <div className="font-display font-bold text-white">Mobi</div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#22D3EE] inline-flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> on the line with {playerName}
                    </div>
                  </div>
                </div>
                <div className="mt-4 relative bg-[#22D3EE]/10 border border-[#22D3EE]/30 rounded-xl rounded-tl-none p-4 text-sm text-white leading-relaxed" data-testid="dial-mobi-hint">
                  {askHint}
                </div>
                <button
                  onClick={() => { setDial(null); onProgress?.({ answers: { ...answersRef.current }, cleared: clearedRef.current, current_q: idx, status: "playing", lifelines: lifelinesRef.current, picked, revealed: false, dial: null, hint: askHint }); }}
                  data-testid="dial-mobi-close"
                  className="mt-5 w-full py-2.5 rounded-md bg-[#22D3EE] text-[#0A0C10] font-bold text-sm hover:bg-[#38BDF8] transition-colors"
                >
                  Got it — thanks Mobi! ▶ Resume
                </button>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Timer({ seconds }) {
  const pct = (seconds / QUESTION_SECONDS) * 100;
  const color = seconds <= 5 ? "#EF4444" : seconds <= 10 ? "#FFD600" : "#10B981";
  return (
    <div className="relative w-14 h-14" data-testid="champ-timer">
      <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
        <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
        <circle cx="18" cy="18" r="16" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
          strokeDasharray={`${pct} 100`} pathLength="100" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-bold text-sm font-mono" style={{ color }}>{seconds}</div>
    </div>
  );
}

function Lifeline({ label, icon: Icon, disabled, onClick, color, textColor, testid }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-xs transition-all disabled:opacity-30 disabled:line-through hover:scale-105"
      style={{ background: color, color: textColor }}>
      {Icon ? <Icon size={12} /> : null} {label}
    </button>
  );
}

/* ChampMirrorStage — the EXACT millionaire studio look, read-only, driven by
 * the shared remote-access session. Rendered for the teacher and all
 * non-controlling participants so everyone sees the player's screen live. */
export function ChampMirrorStage({ questions = [], title = "Champ Student", session = {}, playerLabel = "Player" }) {
  const total = questions.length;
  const qIdx = total ? Math.max(0, Math.min(session.current_q || 0, total - 1)) : 0;
  const q = questions[qIdx] || { options: [] };
  const picked = session.picked;
  const revealed = !!session.revealed;
  const hidden = session.hidden || [];
  const lifelines = session.lifelines || { fifty: true, ask: true, skip: true };
  const finished = session.status === "final";
  const ladderTier = LADDER[Math.max(0, LADDER.length - qIdx - 1)] || LADDER[LADDER.length - 1];

  if (finished) {
    return (
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#1A0F2E] via-[#2D1B4E] to-[#0A0C10] text-white flex flex-col items-center justify-center min-h-[calc(100vh-7rem)]" data-testid="champ-mirror-finished">
        <Crown size={56} className="text-[#FFD600] mb-4" />
        <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#FFD600] font-bold">/ champ student · finished</div>
        <h1 className="font-display font-bold text-5xl tracking-tighter mt-3">{session.cleared || 0} / {total} cleared</h1>
        <p className="text-white/70 mt-2 text-lg">{playerLabel} finished the run.</p>
      </div>
    );
  }

  return (
    <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#1A0F2E] via-[#2D1B4E] to-[#1E1B3D] text-white min-h-[calc(100vh-7rem)]" data-testid="champ-remote-mirror">
      {/* Header */}
      <div className="absolute top-4 left-5 right-5 flex items-center justify-between z-10">
        <div className="inline-flex items-center gap-2">
          <GraduationCap size={16} className="text-[#FFD600]" />
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] font-bold">Champ Student</div>
        </div>
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/15 border border-red-400/40 text-red-300 text-[10px] font-mono uppercase tracking-wider">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> live · {playerLabel} playing
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_260px] pt-20 pb-6 px-6 lg:px-10 gap-6">
        <div className="flex flex-col min-w-0">
          <div className="flex items-center justify-between mb-6">
            <div className="text-sm font-mono uppercase tracking-[0.22em] text-white/70">QUESTION {qIdx + 1} OF {total}</div>
            <div className="px-3 py-1.5 bg-[#FFD600]/20 border border-[#FFD600]/40 rounded-md text-[#FFD600] text-sm font-bold font-mono">For {ladderTier.prize}</div>
          </div>

          <div className="bg-gradient-to-br from-[#1E40AF] to-[#0F2A6B] border border-[#FFD600]/30 rounded-2xl p-8 lg:p-10 text-center shadow-[0_0_60px_-15px_rgba(255,214,0,0.4)]">
            <div className="font-display font-bold text-2xl lg:text-3xl leading-snug">{q.question}</div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mt-6">
            {(q.options || []).map((opt, i) => {
              const ci = correctIndexOf(q);
              const isHidden = hidden.includes(i);
              const isPicked = picked === i;
              const isCorrect = revealed && i === ci;
              const isWrong = revealed && isPicked && i !== ci;
              return (
                <div
                  key={i}
                  data-testid={`champ-mirror-opt-${i}`}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    isHidden ? "opacity-20 bg-[#1E40AF]/30 border-[#1E40AF]" :
                    isCorrect ? "bg-[#10B981] border-[#10B981] scale-[1.02]" :
                    isWrong ? "bg-red-600 border-red-600" :
                    isPicked ? "bg-[#FFD600] text-[#0A0C10] border-[#FFD600]" :
                    "bg-gradient-to-br from-[#1E40AF]/80 to-[#0F2A6B]/80 border-[#FFD600]/30"
                  }`}
                >
                  <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-bold text-sm mr-3 ${isCorrect || isWrong || isPicked ? "bg-white/20" : "bg-[#FFD600] text-[#0A0C10]"}`}>{"ABCD"[i]}</span>
                  <span className="font-medium">{isHidden ? "—" : opt}</span>
                  {isPicked && !revealed && <span className="ml-2 text-[9px] font-mono uppercase tracking-wider opacity-70">◉ locked in</span>}
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <Lifeline label="50:50" disabled={!lifelines.fifty} onClick={() => {}} color="#FFD600" textColor="#0A0C10" testid="champ-mirror-fifty" />
            <Lifeline label="Dial Mobi" icon={Phone} disabled={!lifelines.ask} onClick={() => {}} color="#22D3EE" textColor="#0A0C10" testid="champ-mirror-dial" />
            <Lifeline label="Skip" icon={FastForward} disabled={!lifelines.skip} onClick={() => {}} color="#EC4899" textColor="#fff" testid="champ-mirror-skip" />
            <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-white/40">watching live · updates every 2.5s</span>
          </div>
          {session.dial === "dialing" && (
            <div className="mt-3 px-4 py-2 bg-[#22D3EE]/15 border border-[#22D3EE]/30 rounded-md text-sm inline-flex items-center gap-2" data-testid="mirror-dialing">
              <PhoneCall size={13} className="text-[#22D3EE] animate-pulse" /> {playerLabel} is dialing Mobi… timer paused
            </div>
          )}
          {session.hint && (
            <div className="mt-3 px-4 py-2 bg-[#22D3EE]/15 border border-[#22D3EE]/30 rounded-md text-sm" data-testid="mirror-hint">
              <span className="text-[#22D3EE] font-mono text-[10px] uppercase tracking-wider mr-2">Mobi says:</span>{session.hint}
            </div>
          )}
        </div>

        {/* Prize ladder */}
        <div className="hidden lg:flex flex-col bg-gradient-to-b from-[#0A0C10] to-[#1A0F2E] rounded-xl border border-[#FFD600]/30 p-3 overflow-hidden" data-testid="champ-mirror-ladder">
          <div className="text-center text-[11px] font-mono uppercase tracking-[0.22em] text-[#FFD600] font-bold mb-2">Prize Ladder</div>
          <div className="space-y-0.5 overflow-y-auto">
            {LADDER.map((tier) => {
              const isCurrent = tier.i === (qIdx + 1);
              return (
                <div key={tier.i} className={`flex items-center justify-between px-3 py-1.5 rounded text-sm transition-all ${
                  isCurrent ? "bg-[#FFD600] text-[#0A0C10] scale-[1.04] shadow-md" :
                  tier.safe ? "bg-[#7C3AED]/30 text-[#FFD600] font-mono" :
                  "text-white/60 font-mono"
                }`}>
                  <span className="font-bold">{tier.i}</span>
                  <span>{tier.prize}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
